import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import useMediaQuery from 'use-media-antd-query';

import { PAGE_NAMES, USER_ROLE } from '~/shared/constants';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import { checkIsAdminPanel, checkIsClientsPanel, checkIsPlatform } from '~/shared/helpers/check-helpers';
import { useTypedTranslation } from '~/shared/hooks';

type MenuItem = Readonly<{
  key: string;
  label: string;
  href?: string;
  popup?: 'blog' | 'courses';
  activePaths?: string[];
  excludePaths?: string[];
}>;

type UseMenuProps = Readonly<{
  lng: string;
  user?: { role?: string | null } | null;
  onMobileMenuClose?: () => void;
  isAdminPanel?: boolean;
  isPlatform?: boolean;
}>;

const useMenu = ({ user, lng }: UseMenuProps) => {
  const t = useTypedTranslation(['page', 'link', 'text']);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const isAdminPanel = checkIsAdminPanel(pathname);
  const isPlatform = checkIsPlatform(pathname);
  const isClientsPanel = checkIsClientsPanel(pathname);
  const [openPopup, setOpenPopup] = useState<'blog' | 'courses' | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const colSize = useMediaQuery();
  const isSmall = colSize === 'xs' || colSize === 'sm' || colSize === 'md';
  const headerHeight = isSmall ? 48 : 64;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const items: MenuItem[] = useMemo(() => {
    const adminItems: MenuItem[] =
      isAdminPanel && user?.role === USER_ROLE.AUTHOR
        ? [
            {
              key: getLocaleLink(PAGE_NAMES.ADMIN_POSTS, lng),
              label: t('page.admin.posts.menu-item'),
              href: getLocaleLink(PAGE_NAMES.ADMIN_POSTS, lng),
            },
          ]
        : isAdminPanel && user?.role === USER_ROLE.ADMIN
          ? [
              {
                key: getLocaleLink(PAGE_NAMES.ADMIN, lng),
                label: t('page.admin.menu-item'),
                href: getLocaleLink(PAGE_NAMES.ADMIN, lng),
              },
              {
                key: getLocaleLink(PAGE_NAMES.ADMIN_POSTS, lng),
                label: t('page.admin.posts.menu-item'),
                href: getLocaleLink(PAGE_NAMES.ADMIN_POSTS, lng),
              },
              {
                key: getLocaleLink(PAGE_NAMES.ADMIN_COMPANIES, lng),
                label: t('page.admin.companies.menu-item'),
                href: getLocaleLink(PAGE_NAMES.ADMIN_COMPANIES, lng),
              },
              {
                key: getLocaleLink(PAGE_NAMES.ADMIN_CATEGORIES, lng),
                label: t('page.admin.categories.menu-item'),
                href: getLocaleLink(PAGE_NAMES.ADMIN_CATEGORIES, lng),
              },
              {
                key: getLocaleLink(PAGE_NAMES.ADMIN_CONSULTATIONS, lng),
                label: t('page.admin.consultations.menu-item'),
                href: getLocaleLink(PAGE_NAMES.ADMIN_CONSULTATIONS, lng),
              },
              {
                key: getLocaleLink(PAGE_NAMES.ADMIN_USERS, lng),
                label: t('page.admin.users.menu-item'),
                href: getLocaleLink(PAGE_NAMES.ADMIN_USERS, lng),
              },
            ]
          : [];

    const platformItems: MenuItem[] = [];
    if (isPlatform) {
      const isCoursesPath =
        pathname.startsWith(getLocaleLink(PAGE_NAMES.PLATFORM_COURSES, lng)) ||
        pathname.startsWith(getLocaleLink(PAGE_NAMES.PLATFORM_ONLINE_COURSE_WITHOUT_ALIAS, lng)) ||
        pathname.startsWith(getLocaleLink(PAGE_NAMES.PLATFORM_VIDEO_COURSE_WITHOUT_ALIAS, lng)) ||
        pathname.startsWith('/' + lng + '/platform/video-courses') ||
        pathname.startsWith('/' + lng + '/platform/online-courses');

      const platformCoursesKey = isCoursesPath ? pathname : getLocaleLink(PAGE_NAMES.PLATFORM_COURSES, lng);

      if (
        user?.role === USER_ROLE.SUPER_TEACHER ||
        user?.role === USER_ROLE.TEACHER ||
        user?.role === USER_ROLE.ADMIN ||
        user?.role === USER_ROLE.STUDENT
      ) {
        platformItems.push({
          label: t('page.platform.courses.menu-item'),
          href: getLocaleLink(PAGE_NAMES.PLATFORM_COURSES, lng),
          key: platformCoursesKey,
          activePaths: [
            getLocaleLink(PAGE_NAMES.PLATFORM_COURSES, lng),
            getLocaleLink(PAGE_NAMES.PLATFORM_VIDEO_COURSES, lng),
          ],
        });
      }

      if (
        user?.role === USER_ROLE.SUPER_TEACHER ||
        user?.role === USER_ROLE.TEACHER ||
        user?.role === USER_ROLE.STUDENT
      ) {
        platformItems.push({
          label: t('page.platform.calendar.menu-item'),
          href: getLocaleLink(PAGE_NAMES.PLATFORM_CALENDAR, lng),
          key: getLocaleLink(PAGE_NAMES.PLATFORM_CALENDAR, lng),
        });
      }

      if (user?.role === USER_ROLE.SUPER_TEACHER || user?.role === USER_ROLE.TEACHER) {
        platformItems.push({
          label: t('page.platform.students-board.menu-item'),
          href: getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_BOARD, lng),
          key: getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_BOARD, lng),
          activePaths: [
            getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_BOARD, lng),
            getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_LIST, lng),
          ],
        });
      }

      if (user?.role === USER_ROLE.SUPER_TEACHER) {
        platformItems.push(
          {
            label: t('page.platform.teachers.menu-item'),
            href: getLocaleLink(PAGE_NAMES.PLATFORM_TEACHERS, lng),
            key: getLocaleLink(PAGE_NAMES.PLATFORM_TEACHERS, lng),
          },
          {
            label: t('page.platform.managers.menu-item'),
            href: getLocaleLink(PAGE_NAMES.PLATFORM_MANAGERS, lng),
            key: getLocaleLink(PAGE_NAMES.PLATFORM_MANAGERS, lng),
          },
        );
      }

      if (user?.role === USER_ROLE.MANAGER) {
        platformItems.push({
          label: t('page.platform.students-list.menu-item'),
          href: getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_LIST, lng),
          key: getLocaleLink(PAGE_NAMES.PLATFORM_STUDENTS_LIST, lng),
        });
      }
    }

    if (isPlatform && (user?.role === USER_ROLE.TEACHER || user?.role === USER_ROLE.SUPER_TEACHER)) {
      platformItems.push({ key: getLocaleLink('/platform/materials', lng),
        href: getLocaleLink('/platform/materials', lng), label: t('text.materials-library') });
    }

    const webItems: MenuItem[] =
      !isAdminPanel && !isPlatform
        ? [
            {
              key: `/${lng}`,
              label: t('page.home.menu-item'),
              href: getLocaleLink(PAGE_NAMES.HOME, lng),
            },
            {
              key: getLocaleLink(PAGE_NAMES.BLOG, lng),
              label: t('page.blog.menu-item'),
              href: getLocaleLink(PAGE_NAMES.BLOG, lng),
              popup: 'blog',
              activePaths: [getLocaleLink(PAGE_NAMES.BLOG, lng)],
            },
            {
              key: getLocaleLink(PAGE_NAMES.COURSES, lng),
              label: t('page.courses.menu-item'),
              href: getLocaleLink(PAGE_NAMES.COURSES, lng),
              popup: 'courses',
              activePaths: [getLocaleLink(PAGE_NAMES.COURSES, lng)],
            },
          ]
        : [];
    return isClientsPanel ? [] : isAdminPanel ? adminItems : isPlatform ? platformItems : webItems;
  }, [isAdminPanel, isClientsPanel, isPlatform, lng, t, user?.role]);

  const closeDropdown = () => {
    setOpenPopup(null);
  };

  return {
    shellRef,
    openPopup,
    closeDropdown,
    setOpenPopup,
    isClientsPanel,
    headerHeight,
    isMounted,
    pathname,
    items,
  };
};

export default useMenu;

